import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Users, 
  Globe, 
  Target, 
  Activity, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Loader
} from 'lucide-react'
import { analyticsAPI, formatters } from '../services/analytics'

function Dashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Buscar dados resumidos de todas as seções
      const [overview, realtime, traffic, conversions] = await Promise.all([
        analyticsAPI.getOverview('7daysAgo'),
        analyticsAPI.getRealtime(),
        analyticsAPI.getTraffic('7daysAgo', 'source', 5),
        analyticsAPI.getConversions('7daysAgo', null, 5)
      ])

      setData({
        overview,
        realtime,
        traffic,
        conversions
      })
    } catch (err) {
      setError(err.message)
      console.error('Erro ao buscar dados do dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-lg text-gray-600">Carregando dashboard...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="mt-4 btn-primary"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  const { overview, realtime, traffic, conversions } = data

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard GA4</h1>
        <p className="mt-2 text-gray-600">
          Resumo completo das suas métricas de analytics
        </p>
      </div>

      {/* Métricas principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Usuários Ativos"
          value={realtime?.activeUsers?.total || 0}
          icon={Activity}
          color="text-green-600"
          bgColor="bg-green-50"
          subtitle="Em tempo real"
        />
        <MetricCard
          title="Sessões (7d)"
          value={overview?.current?.summary?.totalSessions || 0}
          change={overview?.changes?.sessions || 0}
          icon={Users}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <MetricCard
          title="Pageviews (7d)"
          value={overview?.current?.summary?.totalPageviews || 0}
          change={overview?.changes?.pageviews || 0}
          icon={Globe}
          color="text-purple-600"
          bgColor="bg-purple-50"
        />
        <MetricCard
          title="Taxa de Conversão"
          value={`${conversions?.summary?.overallConversionRate || 0}%`}
          change={0} // Implementar comparação depois
          icon={Target}
          color="text-orange-600"
          bgColor="bg-orange-50"
        />
      </div>

      {/* Seções de resumo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Tempo Real */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Tempo Real</h3>
            <Link 
              to="/realtime" 
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver detalhes <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Usuários ativos</span>
              <span className="text-2xl font-bold text-green-600">
                {realtime?.activeUsers?.total || 0}
              </span>
            </div>
            {realtime?.usersByCountry?.slice(0, 3).map((country, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-gray-600">{country.country}</span>
                <span className="text-gray-900 font-medium">{country.activeUsers}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Páginas */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Páginas Populares</h3>
            <Link 
              to="/traffic" 
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver detalhes <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-4">
            {traffic?.pages?.slice(0, 5).map((page, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {page.pageTitle}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{page.pagePath}</p>
                </div>
                <span className="text-sm font-medium text-gray-700 ml-4">
                  {formatters.number(page.pageviews)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Fontes de Tráfego */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Fontes de Tráfego</h3>
            <Link 
              to="/traffic" 
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver detalhes <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-4">
            {traffic?.sources?.slice(0, 5).map((source, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {source.channel}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {source.source} / {source.medium}
                  </p>
                </div>
                <span className="text-sm font-medium text-gray-700 ml-4">
                  {formatters.number(source.sessions)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Conversões */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Conversões</h3>
            <Link 
              to="/conversions" 
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver detalhes <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total de conversões</span>
              <span className="text-2xl font-bold text-orange-600">
                {formatters.number(conversions?.summary?.totalConversions || 0)}
              </span>
            </div>
            {conversions?.conversionEvents?.slice(0, 3).map((event, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-gray-600">{event.eventName}</span>
                <span className="text-gray-900 font-medium">
                  {formatters.number(event.conversions)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function MetricCard({ title, value, change, icon: Icon, color, bgColor, subtitle }) {
  const isPositive = change > 0
  const isNegative = change < 0

  return (
    <div className="metric-card">
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        {change !== undefined && (
          <div className={`flex items-center text-sm ${
            isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'
          }`}>
            {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : 
             isNegative ? <TrendingDown className="w-4 h-4 mr-1" /> : null}
            {Math.abs(change)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mt-1">
          {typeof value === 'number' ? formatters.number(value) : value}
        </p>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  )
}

export default Dashboard 